import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  BellRing,
  Bookmark,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  Clock3,
  ExternalLink,
  FileText,
  Gavel,
  Loader2,
  MessageSquare,
  Newspaper,
  RefreshCw,
  Save,
  Settings2,
  ShieldAlert,
  Smartphone,
  Sparkles,
  Workflow,
  X,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { AppPage } from '@/components/layout/AppPage';
import { buildSavedItemWorkspace } from '@/lib/savedItemMetadata';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FRAMEWORK_ITEMS = 4;
const DISCUSSION_PROMPT_MAX = 1400;
const ROLE_MODES = ['PARTNER', 'ASSOCIATE', 'CLIENT'];

function getUploadPreviewUrl(uploadId) {
  return `${API}/uploads/${uploadId}/preview`;
}

function parsePreferenceLines(value) {
  return value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
}

function buildLegacyNewsItem(judgment) {
  return {
    title: judgment.title,
    url: judgment.url,
    summary: judgment.headline || '',
    source: judgment.source || 'Indian Kanoon',
    date: judgment.date || '',
    verification_status: judgment.url?.includes('indiankanoon.org') ? 'CERTIFIED' : 'UNVERIFIED',
    verification_label: judgment.url?.includes('indiankanoon.org') ? 'Certified on Indian Kanoon' : 'Unverified news report',
    verified_case_url: judgment.url?.includes('indiankanoon.org') ? judgment.url : '',
    summary_bullets: judgment.summary_bullets || [],
    matched_queries: [],
    visibility_reasons: [],
    suggested_actions: [],
  };
}

function getSummaryBullets(item) {
  if (Array.isArray(item.summary_bullets) && item.summary_bullets.length) return item.summary_bullets;
  const bullets = [];
  if (item.summary) bullets.push(item.summary);
  if (item.verification_status === 'CERTIFIED') bullets.push('Certified against Indian Kanoon.');
  else bullets.push('This update is still unverified against Indian Kanoon.');
  if (item.matched_queries?.length) bullets.push(`Matched your tracked research: ${item.matched_queries.slice(0, 2).join(', ')}`);
  return bullets.slice(0, 3);
}

function getCompactBrief(item) {
  return item.brief_snippet || item.summary || item.headline || 'Open for a short legal brief.';
}

function getRoleAwareSnippet(item, roleMode) {
  if (roleMode === 'CLIENT' && item.client_safe_summary) return item.client_safe_summary;
  if (roleMode === 'ASSOCIATE' && Array.isArray(item.suggested_actions) && item.suggested_actions.length) {
    return item.suggested_actions[0];
  }
  return getCompactBrief(item);
}

function getExpandedSummary(item, roleMode) {
  if (roleMode === 'CLIENT' && item.client_safe_summary) return item.client_safe_summary;
  return item.expanded_summary || item.summary || item.headline || 'No expanded article brief is available yet.';
}

function getDetailPoints(item) {
  if (Array.isArray(item.key_points) && item.key_points.length) return item.key_points;
  return getSummaryBullets(item);
}

function getWhyItMatters(item) {
  if (Array.isArray(item.why_it_matters) && item.why_it_matters.length) return item.why_it_matters;

  const fallback = [];
  if (item.matched_queries?.length) fallback.push(`Relevant to your tracked research: ${item.matched_queries.slice(0, 2).join(', ')}`);
  if (item.verification_status === 'CERTIFIED') fallback.push('This report is linked to a matching Indian Kanoon result for faster legal follow-up.');
  else fallback.push('This report is still awaiting Indian Kanoon confirmation, so it should be treated as a monitored update.');
  if (item.source) fallback.push(`Source being monitored: ${item.source}.`);
  return fallback.slice(0, 3);
}

function getTimelineItems(item) {
  if (Array.isArray(item.timeline) && item.timeline.length) return item.timeline;
  return [{
    date: item.published_label || item.date || 'Current update',
    event: item.summary || item.headline || item.title || 'Legal development captured in the brief.',
    significance: item.matched_queries?.length
      ? 'This overlaps with a topic you have already researched.'
      : 'This is the main development surfaced from the source article.',
  }];
}

function formatBriefDate(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatShortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

function roleLabel(mode) {
  if (mode === 'PARTNER') return 'Senior scan';
  if (mode === 'ASSOCIATE') return 'Execution mode';
  return 'Client-safe';
}

function priorityTone(priority) {
  if (priority === 'critical' || priority === 'Critical today') return 'is-critical';
  if (priority === 'high' || priority === 'High signal') return 'is-high';
  if (priority === 'medium' || priority === 'Worth reviewing') return 'is-medium';
  return 'is-watch';
}

export default function DailyBriefPage() {
  const { user, setUser, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [articleBriefs, setArticleBriefs] = useState({});
  const [loadingCardKey, setLoadingCardKey] = useState('');
  const [activeArticle, setActiveArticle] = useState(null);
  const [articleError, setArticleError] = useState('');
  const [landingExpanded, setLandingExpanded] = useState(false);
  const [roleMode, setRoleMode] = useState(user?.daily_brief_mode || 'PARTNER');
  const [deliveryTime, setDeliveryTime] = useState(user?.daily_brief_delivery_time || '08:00');
  const [watchlistText, setWatchlistText] = useState((user?.daily_brief_watchlist || []).join('\n'));
  const [mutedText, setMutedText] = useState((user?.daily_brief_muted_terms || []).join('\n'));
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [setupExpanded, setSetupExpanded] = useState(false);
  const attemptedInitialGeneration = useRef(false);

  useEffect(() => {
    setRoleMode(user?.daily_brief_mode || 'PARTNER');
    setDeliveryTime(user?.daily_brief_delivery_time || '08:00');
    setWatchlistText((user?.daily_brief_watchlist || []).join('\n'));
    setMutedText((user?.daily_brief_muted_terms || []).join('\n'));
  }, [user?.daily_brief_mode, user?.daily_brief_delivery_time, user?.daily_brief_watchlist, user?.daily_brief_muted_terms]);

  const hasBriefForDate = useCallback((items, date) => (
    items.some((brief) => new Date(brief.date).toDateString() === date.toDateString())
  ), []);

  const fetchBriefs = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/briefs`, { headers: getAuthHeaders(), credentials: 'include' });
      if (!resp.ok) return [];
      const data = await resp.json();
      setBriefs(data);
      return data;
    } catch {
      return [];
    }
  }, [getAuthHeaders]);

  const generateBrief = useCallback(async ({ silent = false } = {}) => {
    setGenerating(true);
    try {
      const resp = await fetch(`${API}/briefs/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (resp.ok) {
        const brief = await resp.json();
        setBriefs(prev => [brief, ...prev.filter(existing => existing.brief_id !== brief.brief_id)]);
        setSelectedDate(new Date(brief.date));
        if (!silent) toast.success('Brief refreshed!');
        return brief;
      }
      throw new Error('Brief request failed');
    } catch {
      if (!silent) toast.error('Failed to refresh brief');
      return null;
    } finally {
      setGenerating(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    let isActive = true;

    const loadBriefs = async () => {
      setInitializing(true);
      const data = await fetchBriefs();
      if (!isActive) return;

      const today = new Date();
      if (!hasBriefForDate(data, today) && !attemptedInitialGeneration.current) {
        attemptedInitialGeneration.current = true;
        await generateBrief({ silent: true });
      }

      if (isActive) setInitializing(false);
    };

    loadBriefs();
    return () => {
      isActive = false;
    };
  }, [fetchBriefs, generateBrief, hasBriefForDate]);

  const handleSave = async (item) => {
    const tags = [
      'daily-brief',
      item.verification_status === 'CERTIFIED' ? 'certified' : 'unverified',
      ...(item.matched_queries?.length ? ['tracked-update'] : []),
      ...(item.watchlist_hits?.length ? ['watchlist-hit'] : []),
    ];
    try {
      await fetch(`${API}/saved`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          type: 'JUDGMENT',
          title: item.title,
          content: item.summary || item.expanded_summary || '',
          url: item.verified_case_url || item.url,
          tags,
          research_data: {
            project_name: 'Daily Brief',
            verification_status: item.verification_status,
            source_url: item.url,
            verified_case_url: item.verified_case_url || null,
          },
          workspace: buildSavedItemWorkspace({
            user,
            itemType: 'JUDGMENT',
            title: item.title,
            content: item.summary || item.expanded_summary || '',
            tags,
            researchData: { project_name: 'Daily Brief' },
          }),
        }),
      });
      toast.success('Saved to library');
    } catch {
      toast.error('Failed to save item');
    }
  };

  const savePreferences = async () => {
    setSavingPreferences(true);
    try {
      const response = await fetch(`${API}/user/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          daily_brief_mode: roleMode,
          daily_brief_delivery_time: deliveryTime,
          daily_brief_watchlist: parsePreferenceLines(watchlistText),
          daily_brief_muted_terms: parsePreferenceLines(mutedText),
        }),
      });
      if (!response.ok) throw new Error('settings failed');
      const updatedUser = await response.json();
      setUser(updatedUser);
      toast.success('Daily Brief setup updated');
    } catch {
      toast.error('Failed to save Daily Brief setup');
    } finally {
      setSavingPreferences(false);
    }
  };

  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d;
  });

  const briefDates = new Set(briefs.map(b => new Date(b.date).toDateString()));
  const activeDate = selectedDate || (briefs[0]?.date ? new Date(briefs[0].date) : null);
  const currentBrief = activeDate
    ? briefs.find(b => new Date(b.date).toDateString() === activeDate.toDateString())
    : briefs[0];

  const trackedUpdates = currentBrief?.tracked_updates || [];
  const newsItems = currentBrief?.news_items || [];
  const statusMessage = currentBrief?.status_message || '';
  const legacyNewsItems = (!trackedUpdates.length && !newsItems.length)
    ? (currentBrief?.judgments || []).map(buildLegacyNewsItem)
    : [];
  const visibleNewsItems = newsItems.length ? newsItems : legacyNewsItems;
  const frameworkBuckets = currentBrief?.news_frameworks || {};
  const orderedFrameworks = Object.entries(frameworkBuckets)
    .filter(([, items]) => Array.isArray(items) && items.length > 0);

  const overviewCards = currentBrief?.overview_cards || [];
  const actionQueue = currentBrief?.action_queue || [];
  const deltaCards = currentBrief?.delta_digest?.cards || [];
  const matterRadar = currentBrief?.matter_radar || [];
  const upcomingHearings = currentBrief?.upcoming_hearings || [];
  const regulatoryPulse = currentBrief?.regulatory_pulse || [];
  const watchlistHits = currentBrief?.watchlist_hits || [];
  const spotlightNews = currentBrief?.spotlight_news || visibleNewsItems.slice(0, 4);
  const roleViews = currentBrief?.role_views || {};
  const activeRoleView = roleViews[roleMode] || roleViews[currentBrief?.daily_brief_mode] || {};

  const isLandingCollapsed = !landingExpanded;
  const expandLanding = () => setLandingExpanded(true);

  const handleActionClick = useCallback((action) => {
    if (!action) return;
    if (action.route === '/chat' && action.prompt) {
      const params = new URLSearchParams();
      params.set('starter_mode', 'RESEARCH');
      params.set('prefill_query', action.prompt);
      navigate(`/chat?${params.toString()}`);
      return;
    }
    if (action.route) {
      navigate(action.route);
    }
  }, [navigate]);

  const openArticleBrief = async (item, key) => {
    expandLanding();
    setActiveArticle({ item, key });
    setArticleError('');
    if (articleBriefs[key] || !item.url) return;

    setLoadingCardKey(key);
    try {
      const resp = await fetch(`${API}/briefs/article-brief?url=${encodeURIComponent(item.url)}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setArticleBriefs(prev => ({ ...prev, [key]: data }));
      } else {
        setArticleError('We could not expand this article right now. You can still open the source.');
      }
    } catch {
      setArticleError('We could not expand this article right now. You can still open the source.');
    } finally {
      setLoadingCardKey('');
    }
  };

  const openArticleDiscussion = useCallback((article) => {
    if (!article) return;
    const prompt = (
      article.chat_prompt
      || `I am reviewing this Daily Brief article: ${article.title}. Explain whether it affects any of my current uploaded files or active matters, and assess the wider legal and judicial impact.`
    ).slice(0, DISCUSSION_PROMPT_MAX);
    const params = new URLSearchParams();
    params.set('starter_mode', 'RESEARCH');
    params.set('prefill_query', prompt);
    navigate(`/chat?${params.toString()}`);
  }, [navigate]);

  const renderBriefCard = (item, i, section) => {
    const key = `${section}-${i}-${item.title}`;
    const detail = articleBriefs[key];
    const displayItem = detail ? { ...item, ...detail } : item;
    const snippet = getRoleAwareSnippet(displayItem, roleMode);
    const isTracked = section === 'tracked';
    const verificationLabel = displayItem.verification_status === 'CERTIFIED' ? 'Indian Kanoon linked' : 'Source report';
    const firstMatterImpact = displayItem.matter_impacts?.[0];
    const firstReason = displayItem.visibility_reasons?.[0];

    return (
      <article
        key={key}
        className={`daily-brief-tile ${isTracked ? 'daily-brief-tile--tracked' : 'daily-brief-tile--news'}`}
        data-testid={`brief-${section}-${i}`}
      >
        <div className="daily-brief-tile__eyebrow">
          {displayItem.spotlight_title ? (
            <span className="daily-brief-tile__spotlight">{displayItem.spotlight_title}</span>
          ) : null}
          <span className="daily-brief-tile__source">{item.source || 'Indian legal news'}</span>
          <span className={`daily-brief-tile__verification ${displayItem.verification_status === 'CERTIFIED' ? 'is-certified' : 'is-reported'}`}>
            {displayItem.verification_status === 'CERTIFIED' ? <CheckCircle2 size={12} /> : <CircleAlert size={12} />}
            {verificationLabel}
          </span>
          <span className={`daily-brief-tile__priority ${priorityTone(displayItem.priority_tier || displayItem.priority_label)}`}>
            {displayItem.priority_label || 'Monitor'}
          </span>
          {isTracked ? (
            <span className="daily-brief-tile__signal">
              <Sparkles size={12} />
              Tracked
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => openArticleBrief(item, key)}
          className="daily-brief-tile__open"
        >
          <h3 className="daily-brief-tile__title">{displayItem.title}</h3>
          <p className="daily-brief-tile__summary">{snippet}</p>
        </button>
        {displayItem.spotlight_subtitle ? (
          <p className="daily-brief-tile__spotlight-copy">{displayItem.spotlight_subtitle}</p>
        ) : null}
        {firstReason ? (
          <p className="daily-brief-tile__insight">{firstReason}</p>
        ) : null}
        {firstMatterImpact ? (
          <div className="daily-brief-tile__matter">
            <Briefcase size={13} />
            <span>{firstMatterImpact.title}</span>
          </div>
        ) : null}
        <div className="daily-brief-tile__meta">
          <span>{item.date || displayItem.published_label || 'Current update'}</span>
          {item.watchlist_hits?.slice(0, 1).map((term) => (
            <span key={term}>{term}</span>
          ))}
          {displayItem.was_read_recently ? <span>Seen recently</span> : null}
        </div>
        <div className="daily-brief-tile__actions">
          <button type="button" className="daily-brief-tile__action" onClick={() => openArticleBrief(item, key)}>
            Open brief
          </button>
          <button type="button" className="daily-brief-tile__ghost" onClick={() => openArticleDiscussion(displayItem)}>
            <MessageSquare size={13} />
            Discuss
          </button>
          <button type="button" className="daily-brief-tile__ghost" onClick={() => handleSave(displayItem)}>
            <Bookmark size={13} />
            Save
          </button>
        </div>
      </article>
    );
  };

  const renderActionCard = (action, index) => (
    <article key={`${action.kind}-${action.title}-${index}`} className="daily-brief-surface-card daily-brief-action-card">
      <div className="daily-brief-surface-card__eyebrow">
        <span>{action.kind || 'Action'}</span>
        <span className={`daily-brief-chip ${priorityTone(action.priority)}`}>{action.priority || 'medium'}</span>
      </div>
      <h3>{action.title}</h3>
      <p>{action.description}</p>
      <button type="button" className="advoq-btn-ghost text-sm" onClick={() => handleActionClick(action)}>
        {action.cta_label || 'Open'}
        <ArrowRight size={14} />
      </button>
    </article>
  );

  const renderHearingCard = (hearing) => (
    <article key={hearing.matter_id} className="daily-brief-surface-card daily-brief-hearing-card">
      <div className="daily-brief-surface-card__eyebrow">
        <span>{hearing.court_forum || 'Hearing watch'}</span>
        <span className={`daily-brief-chip ${hearing.days_until <= 1 ? 'is-critical' : 'is-high'}`}>{hearing.urgency_label}</span>
      </div>
      <h3>{hearing.title}</h3>
      <p>{hearing.objective}</p>
      <div className="daily-brief-inline-list">
        <span>{formatShortDate(hearing.next_hearing_date)}</span>
        <span>{hearing.authority_count || 0} authority items</span>
      </div>
      {hearing.next_steps?.length ? (
        <ul className="daily-brief-bullet-list">
          {hearing.next_steps.map(step => <li key={step}>{step}</li>)}
        </ul>
      ) : null}
      <div className="daily-brief-surface-card__actions">
        <button type="button" className="advoq-btn-ghost text-sm" onClick={() => navigate(hearing.route)}>
          Hearing prep
        </button>
        <button type="button" className="advoq-btn-ghost text-sm" onClick={() => navigate(hearing.war_room_route)}>
          Matter desk
        </button>
      </div>
    </article>
  );

  const renderRadarCard = (entry, index) => (
    <article key={`${entry.matter_id}-${entry.article_title}-${index}`} className="daily-brief-surface-card daily-brief-radar-card">
      <div className="daily-brief-surface-card__eyebrow">
        <span>{entry.matter_title}</span>
        <span className={`daily-brief-chip ${priorityTone(entry.impact_level)}`}>{entry.impact_level || 'watch'}</span>
      </div>
      <h3>{entry.article_title}</h3>
      <p>{entry.brief_snippet}</p>
      {entry.reasons?.length ? (
        <ul className="daily-brief-bullet-list">
          {entry.reasons.map(reason => <li key={reason}>{reason}</li>)}
        </ul>
      ) : null}
      <div className="daily-brief-surface-card__actions">
        <button type="button" className="advoq-btn-ghost text-sm" onClick={() => navigate(entry.route)}>
          Open matter
        </button>
        <button type="button" className="advoq-btn-ghost text-sm" onClick={() => navigate(entry.hearing_route)}>
          Hearing lane
        </button>
      </div>
    </article>
  );

  const activeArticleDetail = activeArticle
    ? { ...activeArticle.item, ...(articleBriefs[activeArticle.key] || {}) }
    : null;
  const activeArticlePoints = activeArticleDetail ? getDetailPoints(activeArticleDetail) : [];
  const activeArticleTimeline = activeArticleDetail ? getTimelineItems(activeArticleDetail) : [];
  const activeArticleImpact = activeArticleDetail ? getWhyItMatters(activeArticleDetail) : [];
  const activeArticleLoading = activeArticle ? loadingCardKey === activeArticle.key : false;
  const activeArticleFiles = Array.isArray(activeArticleDetail?.relevant_files) ? activeArticleDetail.relevant_files : [];
  const activeArticleMatters = Array.isArray(activeArticleDetail?.relevant_matters) ? activeArticleDetail.relevant_matters : [];
  const briefTitle = activeRoleView.headline || currentBrief?.headline || 'Today’s Daily Brief';

  const emptyState = !trackedUpdates.length && !visibleNewsItems.length;

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="daily-brief-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <AppPage
          width="narrow"
          className={`app-page--landing-locked ${isLandingCollapsed ? 'app-page--landing-collapsed' : 'app-page--landing-expanded'}`}
        >
          <div className={`daily-brief-shell ${isLandingCollapsed ? 'is-collapsed' : 'is-expanded'}`}>
            <section className="daily-brief-hero">
              <div className="daily-brief-hero__main">
                <div className="daily-brief-hero__kicker">
                  <Newspaper size={13} />
                  Daily Brief
                </div>
                <h1 className="daily-brief-hero__title">{briefTitle}</h1>
                <div className="daily-brief-hero__date">{formatBriefDate(activeDate || currentBrief?.date)}</div>
              </div>
              <div className="daily-brief-hero__actions">
                <div className="daily-brief-hero__toolbar">
                  {ROLE_MODES.map(mode => (
                    <button
                      key={mode}
                      type="button"
                      className={`daily-brief-toolbar-button ${roleMode === mode ? 'is-active' : ''}`}
                      onClick={() => setRoleMode(mode)}
                    >
                      {roleLabel(mode)}
                    </button>
                  ))}
                  <button type="button" className="daily-brief-toolbar-button" onClick={expandLanding}>
                    <ChevronDown size={14} />
                    {isLandingCollapsed ? 'Expand' : 'Expanded'}
                  </button>
                  <button data-testid="generate-brief-btn" onClick={() => { expandLanding(); generateBrief(); }} disabled={generating} className="daily-brief-toolbar-button">
                    {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {generating ? 'Refreshing...' : 'Refresh'}
                  </button>
                  <button type="button" className="daily-brief-toolbar-button" onClick={() => { expandLanding(); setSetupExpanded(prev => !prev); }}>
                    <Settings2 size={13} />
                    Setup
                  </button>
                </div>
                <div className="daily-brief-hero__delivery">
                  <span className="daily-brief-hero__delivery-chip">
                    <Smartphone size={13} />
                    {user?.whatsapp_opt_in ? `WhatsApp ${deliveryTime}` : `Manual ${deliveryTime}`}
                  </span>
                </div>
              </div>
            </section>

            <section className="daily-brief-date-rail">
              <div className="daily-brief-date-rail__label">Brief archive</div>
              <div className="daily-brief-date-rail__scroller">
                {last30Days.map((date, i) => {
                  const hasBrief = briefDates.has(date.toDateString());
                  const isSelected = activeDate?.toDateString() === date.toDateString();
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setSelectedDate(date);
                      }}
                      className={`daily-brief-date-rail__item ${isSelected ? 'is-active' : ''}`}
                    >
                      <span className="daily-brief-date-rail__weekday">{date.toLocaleDateString('en', { weekday: 'short' })}</span>
                      <span className="daily-brief-date-rail__day">{date.getDate()}</span>
                      <div className="daily-brief-date-rail__dot" style={{ background: hasBrief ? 'var(--accent-primary)' : 'var(--border-subtle)' }} />
                    </button>
                  );
                })}
              </div>
            </section>

            {isLandingCollapsed ? (
              <section className="daily-brief-section">
                <div className="daily-brief-section__header">
                  <div>
                    <h2 className="daily-brief-section__title">Today&apos;s 4-story brief</h2>
                    <p className="daily-brief-section__copy">What India is reacting to, a story tied to your work, a lighter read, and the case courts are buzzing about.</p>
                  </div>
                </div>
                <div className="daily-brief-tile-grid">
                  {spotlightNews.map((item, i) => renderBriefCard(item, i, 'spotlight'))}
                </div>
                <section className="daily-brief-collapsed-cta">
                  <p className="daily-brief-collapsed-cta__copy">
                    Show more news to open the full brief with matter radar, hearing watch, deltas, and the rest of today&apos;s stories.
                  </p>
                  <button type="button" className="advoq-btn-secondary text-sm" onClick={expandLanding}>
                    Show more news
                  </button>
                </section>
              </section>
            ) : null}

            {currentBrief ? (
              !isLandingCollapsed ? (
              <div className="daily-brief-content">
                <section className="daily-brief-section">
                  <div className="daily-brief-section__header">
                    <div>
                      <h2 className="daily-brief-section__title">Morning control room</h2>
                      <p className="daily-brief-section__copy">A single glance at the counts that decide whether today is routine or needs intervention.</p>
                    </div>
                    <span className="daily-brief-section__count">{overviewCards.length}</span>
                  </div>
                  <div className="daily-brief-kpi-grid">
                    {overviewCards.map((card) => (
                      <article key={card.label} className="daily-brief-kpi-card">
                        <p className="daily-brief-kpi-card__label">{card.label}</p>
                        <p className="daily-brief-kpi-card__value">{card.value}</p>
                        <p className="daily-brief-kpi-card__detail">{card.detail}</p>
                      </article>
                    ))}
                  </div>
                </section>

                <section className="daily-brief-section">
                  <button type="button" className="daily-brief-section__toggle" onClick={() => setSetupExpanded(prev => !prev)}>
                    <div>
                      <h2 className="daily-brief-section__title">Daily Brief setup</h2>
                      <p className="daily-brief-section__copy">Keep this tucked away until you actually need to change delivery, watchlist terms, or muted topics.</p>
                    </div>
                    <span className="daily-brief-section__toggle-meta">
                      {setupExpanded ? 'Hide' : 'Expand'}
                      <ChevronDown size={14} />
                    </span>
                  </button>
                  {setupExpanded ? (
                    <div className="daily-brief-preferences">
                      <div className="daily-brief-preferences__fields">
                        <label className="daily-brief-field">
                          <span>Delivery time</span>
                          <input type="time" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} className="advoq-input" />
                        </label>
                        <label className="daily-brief-field">
                          <span>Watchlist terms</span>
                          <textarea
                            className="advoq-input"
                            rows={4}
                            value={watchlistText}
                            onChange={(e) => setWatchlistText(e.target.value)}
                            placeholder={'Client name\nOpposite party\nSector or regulator'}
                          />
                        </label>
                        <label className="daily-brief-field">
                          <span>Muted terms</span>
                          <textarea
                            className="advoq-input"
                            rows={4}
                            value={mutedText}
                            onChange={(e) => setMutedText(e.target.value)}
                            placeholder={'Recruitment\nEvent coverage\nLaw school news'}
                          />
                        </label>
                      </div>
                      <div className="daily-brief-preferences__footer">
                        <p>Role mode changes update the page immediately. Saving also teaches the backend what kind of brief to generate by default.</p>
                        <button type="button" className="advoq-btn-primary text-sm" onClick={savePreferences} disabled={savingPreferences}>
                          {savingPreferences ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                          {savingPreferences ? 'Saving...' : 'Save Daily Brief setup'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </section>

                <section className="daily-brief-section">
                  <div className="daily-brief-section__header">
                    <div>
                      <h2 className="daily-brief-section__title">What changed since the last brief</h2>
                      <p className="daily-brief-section__copy">Use the delta layer to see movement, not just the feed.</p>
                    </div>
                    <span className="daily-brief-section__count">{deltaCards.length}</span>
                  </div>
                  <div className="daily-brief-surface-grid">
                    {deltaCards.map((card) => (
                      <article key={`${card.type}-${card.title}`} className="daily-brief-surface-card">
                        <div className="daily-brief-surface-card__eyebrow">
                          <span>{card.type || 'Delta'}</span>
                        </div>
                        <h3>{card.title}</h3>
                        <p>{card.summary}</p>
                      </article>
                    ))}
                  </div>
                </section>

                <section className="daily-brief-section">
                  <div className="daily-brief-section__header">
                    <div>
                      <h2 className="daily-brief-section__title">Action queue</h2>
                      <p className="daily-brief-section__copy">Turn the brief into work product without opening a dozen other surfaces first.</p>
                    </div>
                    <span className="daily-brief-section__count">{actionQueue.length}</span>
                  </div>
                  <div className="daily-brief-surface-grid">
                    {actionQueue.map(renderActionCard)}
                  </div>
                </section>

                {upcomingHearings.length > 0 ? (
                  <section className="daily-brief-section">
                    <div className="daily-brief-section__header">
                      <div>
                        <h2 className="daily-brief-section__title">Hearing tomorrow mode</h2>
                        <p className="daily-brief-section__copy">Upcoming listed matters, with the first objective and the next preparation items surfaced directly into the brief.</p>
                      </div>
                      <span className="daily-brief-section__count">{upcomingHearings.length}</span>
                    </div>
                    <div className="daily-brief-surface-grid">
                      {upcomingHearings.map(renderHearingCard)}
                    </div>
                  </section>
                ) : null}

                {matterRadar.length > 0 ? (
                  <section className="daily-brief-section">
                    <div className="daily-brief-section__header">
                      <div>
                        <h2 className="daily-brief-section__title">Matter impact radar</h2>
                        <p className="daily-brief-section__copy">The strongest links between fresh developments and your active matter lanes.</p>
                      </div>
                      <span className="daily-brief-section__count">{matterRadar.length}</span>
                    </div>
                    <div className="daily-brief-surface-grid">
                      {matterRadar.map(renderRadarCard)}
                    </div>
                  </section>
                ) : null}

                {watchlistHits.length > 0 ? (
                  <section className="daily-brief-section">
                    <div className="daily-brief-section__header">
                      <div>
                        <h2 className="daily-brief-section__title">Watchlist hits</h2>
                        <p className="daily-brief-section__copy">Items that fired on your explicit watchlist, separate from passive personalization.</p>
                      </div>
                      <span className="daily-brief-section__count">{watchlistHits.length}</span>
                    </div>
                    <div className="daily-brief-tile-grid">
                      {watchlistHits.map((item, i) => renderBriefCard(item, i, 'watchlist'))}
                    </div>
                  </section>
                ) : null}

                {regulatoryPulse.length > 0 ? (
                  <section className="daily-brief-section">
                    <div className="daily-brief-section__header">
                      <div>
                        <h2 className="daily-brief-section__title">Regulatory pulse</h2>
                        <p className="daily-brief-section__copy">A dedicated lane for compliance and notification-heavy developments that can hit advisory work before the courtroom does.</p>
                      </div>
                      <span className="daily-brief-section__count">{regulatoryPulse.length}</span>
                    </div>
                    <div className="daily-brief-tile-grid">
                      {regulatoryPulse.map((item, i) => renderBriefCard(item, i, 'regulatory'))}
                    </div>
                  </section>
                ) : null}

                <section className="daily-brief-section">
                  <div className="daily-brief-section__header">
                    <div>
                      <h2 className="daily-brief-section__title">Tracked matters and search history</h2>
                      <p className="daily-brief-section__copy">The updates most likely to change active work first.</p>
                    </div>
                    <span className="daily-brief-section__count">{trackedUpdates.length}</span>
                  </div>
                  <div className="daily-brief-tile-grid daily-brief-tile-grid--tracked">
                    {trackedUpdates.map((item, i) => renderBriefCard(item, i, 'tracked'))}
                  </div>
                </section>

                <section className="daily-brief-section">
                  <div className="daily-brief-section__header">
                    <div>
                      <h2 className="daily-brief-section__title">Today’s legal signal</h2>
                      <p className="daily-brief-section__copy">Broader developments worth scanning after your tracked items.</p>
                    </div>
                    <span className="daily-brief-section__count">{visibleNewsItems.length}</span>
                  </div>
                  <div className="daily-brief-tile-grid">
                    {visibleNewsItems.map((item, i) => renderBriefCard(item, i, 'news'))}
                  </div>
                </section>

                {orderedFrameworks.length > 0 ? (
                  <section className="daily-brief-section">
                    <button type="button" className="daily-brief-section__toggle" onClick={expandLanding}>
                      <div>
                        <h2 className="daily-brief-section__title">Framework views</h2>
                        <p className="daily-brief-section__copy">Specialised tracks stay tucked away until you open the full brief.</p>
                      </div>
                      <span className="daily-brief-section__toggle-meta">
                        {orderedFrameworks.length} sections
                        <ChevronDown size={14} />
                      </span>
                    </button>
                    {!isLandingCollapsed ? (
                      <div className="daily-brief-framework-stack">
                        {orderedFrameworks.map(([framework, items]) => (
                          <div key={framework} className="daily-brief-framework-group">
                            <h3 className="daily-brief-framework-group__title">{framework}</h3>
                            <div className="daily-brief-tile-grid">
                              {items.slice(0, MAX_FRAMEWORK_ITEMS).map((item, i) => renderBriefCard(item, i, `framework-${framework}`))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>
                ) : null}

                {emptyState ? (
                  <div className="advoq-card p-6">
                    <p className="text-base font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      No live updates were available for this brief yet.
                    </p>
                    <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                      {statusMessage || 'We could not fetch legal-news items for this run. Try refreshing the brief again in a moment.'}
                    </p>
                  </div>
                ) : null}
              </div>
              ) : null
            ) : (
              <div className="text-center py-16">
                {initializing || generating ? (
                  <Loader2 size={48} className="mx-auto mb-4 animate-spin" style={{ color: 'var(--text-muted)' }} />
                ) : (
                  <Newspaper size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                )}
                <p className="text-lg mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {initializing || generating ? 'Preparing your latest brief' : 'No brief for this date'}
                </p>
                <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                  {initializing || generating
                    ? 'We are pulling fresh legal updates and ranking them around your work.'
                    : 'Refresh the brief to pull the latest Indian legal news and tracked updates.'}
                </p>
              </div>
            )}
          </div>
        </AppPage>
      </main>

      {activeArticleDetail && (
        <div className="fixed inset-0 z-50 px-4 py-6 sm:p-8" style={{ background: 'rgba(5, 10, 16, 0.78)', backdropFilter: 'blur(10px)' }}>
          <div className="mx-auto flex h-full w-full max-w-4xl items-center justify-center">
            <div className="advoq-card w-full max-h-full overflow-hidden">
              <div className="flex items-start justify-between gap-4 px-5 py-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                      {activeArticleDetail.source || 'Indian legal news'}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                      style={activeArticleDetail.verification_status === 'CERTIFIED'
                        ? { background: 'var(--verified-bg)', color: 'var(--verified-green)', border: '1px solid rgba(46,204,90,0.22)' }
                        : { background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.22)' }}
                    >
                      {activeArticleDetail.verification_status === 'CERTIFIED' ? <CheckCircle2 size={12} /> : <CircleAlert size={12} />}
                      {activeArticleDetail.verification_status === 'CERTIFIED' ? 'Indian Kanoon linked' : 'Source article'}
                    </span>
                    {(activeArticleDetail.published_label || activeArticleDetail.date) && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)' }}>
                        {activeArticleDetail.published_label || activeArticleDetail.date}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                    {activeArticleDetail.title}
                  </h2>
                  <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                    {getRoleAwareSnippet(activeArticleDetail, roleMode)}
                  </p>
                </div>
                <button
                  onClick={() => setActiveArticle(null)}
                  className="shrink-0 rounded-full p-2"
                  style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}
                  aria-label="Close article brief"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="overflow-y-auto px-5 py-5" style={{ maxHeight: 'calc(85vh - 84px)' }}>
                {activeArticleLoading && (
                  <div className="flex items-center gap-2 mb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <Loader2 size={14} className="animate-spin" />
                    Building an expanded article brief and timeline...
                  </div>
                )}
                {articleError && !activeArticleLoading && (
                  <div className="mb-4 rounded-lg px-3 py-2 text-sm" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                    {articleError}
                  </div>
                )}

                <div className="space-y-6">
                  <section>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>Expanded Brief</h3>
                    <div className="rounded-2xl p-4 text-sm leading-7" style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}>
                      {getExpandedSummary(activeArticleDetail, roleMode)}
                    </div>
                  </section>

                  {Array.isArray(activeArticleDetail.suggested_actions) && activeArticleDetail.suggested_actions.length > 0 ? (
                    <section>
                      <h3 className="text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>Suggested Actions</h3>
                      <ul className="space-y-2">
                        {activeArticleDetail.suggested_actions.map((point, index) => (
                          <li key={`${point}-${index}`} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--accent-primary)' }}>•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}

                  <section>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>Key Points</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {activeArticlePoints.map((point, index) => (
                        <div key={`${point}-${index}`} className="rounded-2xl p-4 text-sm" style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}>
                          {point}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>Timeline</h3>
                    <div className="space-y-3">
                      {activeArticleTimeline.map((entry, index) => (
                        <div key={`${entry.date}-${index}`} className="rounded-2xl p-4" style={{ background: 'var(--bg-surface)' }}>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] mb-2" style={{ color: 'var(--accent-primary)' }}>
                            {entry.date || `Step ${index + 1}`}
                          </p>
                          <p className="text-sm mb-2" style={{ color: 'var(--text-primary)' }}>{entry.event}</p>
                          {entry.significance && (
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{entry.significance}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>Why This Matters</h3>
                    <ul className="space-y-2">
                      {activeArticleImpact.map((point, index) => (
                        <li key={`${point}-${index}`} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <span style={{ color: 'var(--accent-primary)' }}>•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {activeArticleMatters.length > 0 ? (
                    <section>
                      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                        <div>
                          <h3 className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Relevant Matters</h3>
                          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                            Matter lanes that this article appears to touch most directly.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {activeArticleMatters.map((matter) => (
                          <div key={`${matter.matter_id}-${matter.title}`} className="rounded-2xl p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
                                    <Briefcase size={12} className="inline-block mr-1" />
                                    Matter match
                                  </span>
                                  <span className={`daily-brief-chip ${priorityTone(matter.impact_level)}`}>{matter.impact_level || 'watch'}</span>
                                </div>
                                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{matter.title}</p>
                                {matter.reasons?.length ? (
                                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{matter.reasons[0]}</p>
                                ) : null}
                                {matter.recommended_action ? (
                                  <p className="text-sm mt-2" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>{matter.recommended_action}</p>
                                ) : null}
                              </div>
                              <div className="flex gap-2">
                                <button type="button" className="advoq-btn-ghost text-sm" onClick={() => navigate(matter.route || '/war-room')}>
                                  Open matter
                                </button>
                                <button type="button" className="advoq-btn-ghost text-sm" onClick={() => navigate(matter.hearing_route || '/case')}>
                                  Hearing lane
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  <section>
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <div>
                        <h3 className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Relevant Files</h3>
                        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                          Only files that clear the current relevance threshold are shown here.
                        </p>
                      </div>
                      <button
                        onClick={() => openArticleDiscussion(activeArticleDetail)}
                        className="advoq-btn-ghost text-sm"
                      >
                        <MessageSquare size={14} />
                        Discuss In Chat
                      </button>
                    </div>

                    {activeArticleLoading ? (
                      <div className="rounded-2xl p-4 text-sm" style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
                        Checking your uploaded files against this article…
                      </div>
                    ) : activeArticleFiles.length > 0 ? (
                      <div className="space-y-3">
                        {activeArticleFiles.map((file) => (
                          <div key={file.upload_id || file.filename} className="rounded-2xl p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
                                    <FileText size={12} className="inline-block mr-1" />
                                    Your file
                                  </span>
                                  {Array.isArray(file.matched_terms) && file.matched_terms.length > 0 && file.matched_terms.slice(0, 3).map((term) => (
                                    <span key={`${file.filename}-${term}`} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-base)', color: 'var(--text-secondary)' }}>
                                      {term}
                                    </span>
                                  ))}
                                </div>
                                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{file.filename}</p>
                                {file.summary && (
                                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{file.summary}</p>
                                )}
                                {file.relevant_excerpt && (
                                  <p className="text-sm mt-2" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
                                    {file.relevant_excerpt}
                                  </p>
                                )}
                              </div>
                              {file.upload_id && (
                                <a
                                  href={getUploadPreviewUrl(file.upload_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium inline-flex items-center gap-2"
                                  style={{ color: 'var(--accent-primary)' }}
                                >
                                  Open file <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-2xl p-4 text-sm" style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
                        No current uploaded file cleared the relevance threshold for this article yet.
                      </div>
                    )}
                  </section>

                  {Array.isArray(activeArticleDetail.watchpoints) && activeArticleDetail.watchpoints.length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-muted)' }}>Watch Next</h3>
                      <ul className="space-y-2">
                        {activeArticleDetail.watchpoints.map((point, index) => (
                          <li key={`${point}-${index}`} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--accent-primary)' }}>•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    {activeArticleDetail.url && (
                      <a
                        href={activeArticleDetail.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="advoq-btn-primary text-sm inline-flex items-center gap-2"
                      >
                        Open Source Article <ExternalLink size={14} />
                      </a>
                    )}
                    <button
                      onClick={() => openArticleDiscussion(activeArticleDetail)}
                      className="advoq-btn-ghost text-sm"
                    >
                      <MessageSquare size={14} /> Discuss In Chat
                    </button>
                    {activeArticleDetail.verified_case_url && activeArticleDetail.verified_case_url !== activeArticleDetail.url && (
                      <a
                        href={activeArticleDetail.verified_case_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium inline-flex items-center gap-2"
                        style={{ color: 'var(--verified-green)' }}
                      >
                        Open Indian Kanoon Result <ExternalLink size={14} />
                      </a>
                    )}
                    <button
                      onClick={() => handleSave(activeArticleDetail)}
                      className="text-sm inline-flex items-center gap-2"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Bookmark size={14} /> Save to library
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
